/**
 * VaultClient — typed read+write wrapper around HupperVault on the active chain.
 *
 * Two clients:
 *   - readClient: uses public RPC, no signer needed, for view calls.
 *   - writeClient: uses BrowserProvider signer (wallet). Required for state-changing txs.
 *
 * Throws ContractNotDeployedError if active chain has no vault address yet.
 */
import {
  Contract,
  type ContractRunner,
  JsonRpcProvider,
  BrowserProvider,
  type Eip1193Provider,
} from "ethers";

import HupperVaultAbi from "./HupperVaultAbi.json";
import MockUSDTAbi from "./MockUSDTAbi.json";
import { ACTIVE_CHAIN, type ChainConfig, isVaultDeployed } from "./chains";

export class ContractNotDeployedError extends Error {
  constructor(chain: ChainConfig) {
    super(
      `HupperVault is not yet deployed on ${chain.name} (chainId ${chain.chainId}). ` +
        `Update packages/contracts/deployments/${chain.key}.json after deploy.`
    );
    this.name = "ContractNotDeployedError";
  }
}

function readProvider(chain: ChainConfig = ACTIVE_CHAIN): JsonRpcProvider {
  return new JsonRpcProvider(chain.rpcUrl, chain.chainId);
}

export interface MatchInfo {
  player1: string;
  player2: string;
  stake: bigint;
  active: boolean;
}

/**
 * Read-only client: works without a wallet. Calls view functions only.
 */
export function getReadClient(chain: ChainConfig = ACTIVE_CHAIN) {
  if (!isVaultDeployed(chain)) throw new ContractNotDeployedError(chain);
  const provider = readProvider(chain);
  return buildClient(chain, provider);
}

/**
 * Write client: bound to a BrowserProvider (e.g. window.ethereum).
 * Throws if no wallet detected.
 */
export async function getWriteClient(
  injected: Eip1193Provider,
  chain: ChainConfig = ACTIVE_CHAIN
) {
  if (!isVaultDeployed(chain)) throw new ContractNotDeployedError(chain);
  const provider = new BrowserProvider(injected, chain.chainId);
  const signer = await provider.getSigner();
  return buildClient(chain, signer);
}

function buildClient(chain: ChainConfig, runner: ContractRunner) {
  const vault = new Contract(chain.vaultAddress!, HupperVaultAbi, runner);
  const usdt = new Contract(chain.usdtAddress!, MockUSDTAbi, runner);

  return {
    chain,
    vault,
    usdt,

    // ─── Reads ────────────────────────────────────────────────────
    async paidMode(): Promise<boolean> {
      return await vault.paidMode();
    },

    async vaultBalanceOf(user: string): Promise<bigint> {
      return await vault.balanceOf(user);
    },

    async usdtBalanceOf(user: string): Promise<bigint> {
      return await usdt.balanceOf(user);
    },

    async usdtAllowance(user: string): Promise<bigint> {
      return await usdt.allowance(user, chain.vaultAddress!);
    },

    async treasuryBalance(): Promise<bigint> {
      return await vault.treasuryBalance();
    },

    async getMatch(matchId: string): Promise<MatchInfo> {
      const [player1, player2, stake, active] = await vault.matches(matchId);
      return { player1, player2, stake, active };
    },

    // ─── Writes (require write client) ────────────────────────────
    async approveUSDT(amount: bigint) {
      const tx = await usdt.approve(chain.vaultAddress!, amount);
      return await tx.wait();
    },

    async deposit(amount: bigint) {
      const tx = await vault.deposit(amount);
      return await tx.wait();
    },

    async withdraw(amount: bigint) {
      const tx = await vault.withdraw(amount);
      return await tx.wait();
    },

    /**
     * Transfer USDT from the connected wallet to an arbitrary address.
     * Used for sending USDT to an external wallet (Binance, hardware wallet, etc.).
     */
    async transferUSDT(to: string, amount: bigint) {
      const tx = await usdt.transfer(to, amount);
      return await tx.wait();
    },

    /**
     * Mint mock USDT. Only works on chains where MockUSDT is deployed (anvil, amoy testnet).
     * Throws on mainnet.
     */
    async mintMockUSDT(to: string, amount: bigint) {
      if (chain.key === "polygon") {
        throw new Error("Cannot mint on mainnet — USDT is real");
      }
      const tx = await usdt.mint(to, amount);
      return await tx.wait();
    },
  };
}

export type VaultClient = Awaited<ReturnType<typeof getWriteClient>>;
